const GEOCODE_CACHE = new Map();

function getAzureMapsKey() {
  return (process.env.REACT_APP_AZURE_MAPS_KEY || '').trim();
}

function normalizeAddress(address) {
  return String(address || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function isPlaceholderAddress(address) {
  const value = String(address || '').trim();
  if (!value) {
    return true;
  }

  return /^(ikke oppgitt|ukjent|n\/a|-)$/i.test(value);
}

/**
 * Geocode a free-text address with Azure Maps Search (Norway).
 * Returns { lat, lng, formattedAddress } or null.
 */
export async function geocodeAddress(address) {
  if (isPlaceholderAddress(address)) {
    return null;
  }

  const cacheKey = normalizeAddress(address);
  if (GEOCODE_CACHE.has(cacheKey)) {
    return GEOCODE_CACHE.get(cacheKey);
  }

  const subscriptionKey = getAzureMapsKey();
  if (!subscriptionKey) {
    console.warn('REACT_APP_AZURE_MAPS_KEY is not set; nearby lock suggestions are disabled.');
    GEOCODE_CACHE.set(cacheKey, null);
    return null;
  }

  const url = new URL('https://atlas.microsoft.com/search/address/json');
  url.searchParams.set('api-version', '1.0');
  url.searchParams.set('subscription-key', subscriptionKey);
  url.searchParams.set('countrySet', 'NO');
  url.searchParams.set('limit', '1');
  url.searchParams.set('query', String(address).trim());

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      console.warn('Azure Maps geocode failed:', response.status, await response.text());
      GEOCODE_CACHE.set(cacheKey, null);
      return null;
    }

    const payload = await response.json();
    const result = payload?.results?.[0];
    const position = result?.position;
    const lat = Number(position?.lat);
    const lng = Number(position?.lon);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      GEOCODE_CACHE.set(cacheKey, null);
      return null;
    }

    const geocoded = {
      lat,
      lng,
      formattedAddress: result?.address?.freeformAddress || String(address).trim()
    };

    GEOCODE_CACHE.set(cacheKey, geocoded);
    return geocoded;
  } catch (error) {
    console.warn('Azure Maps geocode error:', error);
    GEOCODE_CACHE.set(cacheKey, null);
    return null;
  }
}

/**
 * True when the geocoded address includes a house/street number.
 * Apartment/unit (H0101 etc.) is optional free text on top of this.
 */
export function addressHasStreetNumber(address) {
  const value = String(address || '').trim();
  if (!value) {
    return false;
  }
  // Norwegian style: "Storgata 12", "Storgata 12A", "12 Storgata"
  return /\d/.test(value);
}

function formatSuggestionLabel(address) {
  const streetName = address?.streetName || address?.street || '';
  const streetNumber = address?.streetNumber || '';
  const municipality =
    address?.municipality ||
    address?.municipalitySubdivision ||
    address?.countrySecondarySubdivision ||
    '';
  const postalCode = address?.postalCode || '';
  const freeform = address?.freeformAddress || '';

  if (streetName && streetNumber) {
    const place = [postalCode, municipality].filter(Boolean).join(' ');
    return place ? `${streetName} ${streetNumber}, ${place}` : `${streetName} ${streetNumber}`;
  }

  return freeform;
}

/**
 * Address autocomplete suggestions for Norway (Azure Maps Fuzzy Search).
 * Prefers point addresses with husnummer over street-only hits.
 * @returns {Promise<Array<{ formattedAddress: string, lat: number, lng: number, hasStreetNumber: boolean }>>}
 */
export async function suggestAddresses(query, { limit = 8 } = {}) {
  const q = String(query || '').trim();
  if (q.length < 3 || isPlaceholderAddress(q)) {
    return [];
  }

  const subscriptionKey = getAzureMapsKey();
  if (!subscriptionKey) {
    return [];
  }

  const url = new URL('https://atlas.microsoft.com/search/address/json');
  url.searchParams.set('api-version', '1.0');
  url.searchParams.set('subscription-key', subscriptionKey);
  url.searchParams.set('countrySet', 'NO');
  url.searchParams.set('typeahead', 'true');
  // Prefer address points / address ranges over bare streets when available
  url.searchParams.set('idxSet', 'PAD,Addr,Str');
  url.searchParams.set('limit', String(Math.min(Math.max(limit, 1), 20)));
  url.searchParams.set('query', q);

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      console.warn('Azure Maps suggest failed:', response.status, await response.text());
      return [];
    }

    const payload = await response.json();
    const seen = new Set();
    const suggestions = [];

    for (const result of payload?.results || []) {
      const addr = result?.address || {};
      const streetNumber = String(addr.streetNumber || '').trim();
      const formattedAddress = formatSuggestionLabel(addr);
      const lat = Number(result?.position?.lat);
      const lng = Number(result?.position?.lon);
      if (!formattedAddress || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }
      const hasStreetNumber = Boolean(streetNumber) || addressHasStreetNumber(formattedAddress);
      const key = normalizeAddress(formattedAddress);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      suggestions.push({
        formattedAddress,
        lat,
        lng,
        hasStreetNumber,
        streetNumber: streetNumber || null
      });
    }

    // Show numbered addresses first so users don't pick a street-only hit
    suggestions.sort((a, b) => Number(b.hasStreetNumber) - Number(a.hasStreetNumber));
    return suggestions.slice(0, limit);
  } catch (error) {
    console.warn('Azure Maps suggest error:', error);
    return [];
  }
}

export function clearGeocodeCache() {
  GEOCODE_CACHE.clear();
}

export function hasAzureMapsKey() {
  return Boolean(getAzureMapsKey());
}
